import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { StrictMode, useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  actionItemPriorities,
  actionItemStatusFilters,
  activeActionItemStatuses,
  activeContextItemStatuses,
  activeDeliveryStatuses,
  contextItemKinds,
  contextItemStatusFilters,
  deliveryStatusFilters,
  linkedinActionItemCategory,
  taskQueueExcludedCategories,
  type ActionItemLifecycleResponse,
  type ActionItemRow,
  type ActionItemsListResponse,
  type ContextItemLifecycleResponse,
  type ContextItemRow,
  type ContextItemsListResponse,
  type DeliveriesListResponse,
  type DeliveryLifecycleResponse,
  type DeliveryRow,
  type RealtimeConfigResponse,
  type SessionAccess,
  type SessionAccessResponse,
  type StatsResponse,
  type TodayResponse
} from "@hermes-personal-assistant/shared";
import { oauthProviderLabel, oauthProviders, oauthRedirectTo, magicLinkSignInOptions, type OAuthProvider } from "./auth";
import { deliveryCardSummary, deliveryTimeline, type DeliveryTimelineEntry } from "./delivery-display";
import { canAccessSection, resolveSection, sectionPath, type AppSection } from "./navigation";
import { synopsisSections } from "./synopsis-display";
import "./styles.css";

type ApiHealth = {
  status: string;
  service: string;
  environment: string;
};

type LoadState<T> = {
  loading: boolean;
  data: T | null;
  error: string | null;
};

type DebugEntry = {
  label: string;
  method: string;
  path: string;
  status: number | null;
  ok: boolean;
  body: unknown;
  at: string;
};

type AuthMode = "magic" | "password";
type WorkQueueTab = "tasks" | "events" | "deliveries" | "linkedin";
type PendingMutationKind = "action_items" | "context_items" | "deliveries";
type LoadOptions = { showLoading?: boolean };
type PendingMutation = {
  kind: PendingMutationKind;
  id: string;
  action: string;
};

const priorityChipOrder = ["urgent", "high", "normal", "low"] as const;

type RealtimeState = {
  status: "idle" | "connecting" | "live" | "error";
  message: string;
  lastSignalAt: string | null;
};

const initialRealtimeState: RealtimeState = {
  status: "idle",
  message: "Live updates start after sign-in.",
  lastSignalAt: null
};

type ProviderAvailability = Record<OAuthProvider, boolean | null>;

const defaultProviderAvailability: ProviderAvailability = { google: null, apple: null };

type Config = {
  apiBaseUrl: string;
  supabaseUrl: string;
  supabasePublishableKey: string;
};

const config: Config = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? "",
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? "",
  supabasePublishableKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? ""
};

const clientConfigured = Boolean(config.apiBaseUrl && config.supabaseUrl && config.supabasePublishableKey);
const supabase: SupabaseClient | null = clientConfigured
  ? createClient(config.supabaseUrl, config.supabasePublishableKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    })
  : null;

const initialLoadState = <T,>(): LoadState<T> => ({ loading: false, data: null, error: null });

function App() {
  const [section, setSection] = useState<AppSection>(() => resolveSection(window.location.pathname));
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authMessage, setAuthMessage] = useState<string | null>(null);
  const [providerAvailability, setProviderAvailability] = useState<ProviderAvailability>(defaultProviderAvailability);
  const [health, setHealth] = useState<LoadState<ApiHealth>>(initialLoadState);
  const [sessionAccess, setSessionAccess] = useState<LoadState<SessionAccessResponse>>(initialLoadState);
  const [today, setToday] = useState<LoadState<TodayResponse>>(initialLoadState);
  const [actionItems, setActionItems] = useState<LoadState<ActionItemsListResponse>>(initialLoadState);
  const [linkedinItems, setLinkedinItems] = useState<LoadState<ActionItemsListResponse>>(initialLoadState);
  const [contextItems, setContextItems] = useState<LoadState<ContextItemsListResponse>>(initialLoadState);
  const [deliveries, setDeliveries] = useState<LoadState<DeliveriesListResponse>>(initialLoadState);
  const [stats, setStats] = useState<LoadState<StatsResponse>>(initialLoadState);
  const [realtime, setRealtime] = useState<RealtimeState>(initialRealtimeState);
  const [debugEntry, setDebugEntry] = useState<DebugEntry | null>(null);
  const [actionStatus, setActionStatus] = useState("active");
  const [actionPriorities, setActionPriorities] = useState<string[]>([]);
  const [actionReview, setActionReview] = useState("all");
  const [linkedinStatus, setLinkedinStatus] = useState("active");
  const [linkedinReview, setLinkedinReview] = useState("all");
  const [contextStatus, setContextStatus] = useState("active");
  const [contextKind, setContextKind] = useState("event");
  const [deliveryStatus, setDeliveryStatus] = useState("active");
  const [activeWorkQueue, setActiveWorkQueue] = useState<WorkQueueTab>("tasks");
  const [pendingMutations, setPendingMutations] = useState<PendingMutation[]>([]);

  useEffect(() => {
    const handlePopState = () => setSection(resolveSection(window.location.pathname));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!supabase) {
      setAuthReady(true);
      return;
    }

    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) {
        return;
      }
      setSession(data.session);
      setAuthReady(true);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setAuthReady(true);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!clientConfigured) {
      return;
    }

    let mounted = true;
    fetch(`${config.supabaseUrl.replace(/\/+$/, "")}/auth/v1/settings`, {
      headers: { apikey: config.supabasePublishableKey }
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((settings: { external?: Partial<Record<OAuthProvider | "email", boolean>> } | null) => {
        if (!mounted || !settings?.external) {
          return;
        }
        setProviderAvailability({
          google: Boolean(settings.external.google),
          apple: Boolean(settings.external.apple)
        });
      })
      .catch(() => {
        // Keep the login usable if the public settings diagnostic is temporarily unreachable.
      });

    return () => {
      mounted = false;
    };
  }, []);

  const apiBase = useMemo(() => config.apiBaseUrl.replace(/\/+$/, ""), []);
  const userEmail = session?.user.email ?? "Signed out";
  const signedIn = Boolean(session);
  const sessionAccessLevel: SessionAccess | null = sessionAccess.data?.access ?? null;
  const canMutateAssistantData = sessionAccess.data?.permissions.mutate_assistant_data ?? false;

  const navigateTo = useCallback((nextSection: AppSection) => {
    const path = sectionPath[nextSection];
    if (window.location.pathname !== path) {
      window.history.pushState(null, "", path);
    }
    setSection(nextSection);
  }, []);

  useEffect(() => {
    if (!authReady) {
      return;
    }

    if (signedIn && section === "login") {
      navigateTo("app");
      return;
    }

    if (!signedIn && section === "app") {
      navigateTo("login");
    }
  }, [authReady, navigateTo, section, signedIn]);

  const runApi = useCallback(
    async <T,>(label: string, path: string, options: RequestInit = {}, requireAuth = true): Promise<T> => {
      const method = options.method ?? "GET";
      const headers = new Headers(options.headers);
      headers.set("accept", "application/json");

      if (options.body && !headers.has("content-type")) {
        headers.set("content-type", "application/json");
      }

      if (requireAuth) {
        if (!session?.access_token) {
          throw new Error("Sign in first to continue.");
        }
        headers.set("authorization", `Bearer ${session.access_token}`);
      }

      let response: Response | null = null;
      let parsedBody: unknown = null;

      try {
        response = await fetch(`${apiBase}${path}`, {
          ...options,
          method,
          headers
        });
        const text = await response.text();
        parsedBody = text ? JSON.parse(text) : null;

        setDebugEntry({
          label,
          method,
          path,
          status: response.status,
          ok: response.ok,
          body: parsedBody,
          at: new Date().toISOString()
        });

        if (!response.ok) {
          const error = typeof parsedBody === "object" && parsedBody && "error" in parsedBody
            ? String((parsedBody as { error: unknown }).error)
            : `HTTP ${response.status}`;
          throw new Error(error);
        }

        return parsedBody as T;
      } catch (error) {
        setDebugEntry({
          label,
          method,
          path,
          status: response?.status ?? null,
          ok: false,
          body: parsedBody ?? { error: error instanceof Error ? error.message : "request_failed" },
          at: new Date().toISOString()
        });
        throw error;
      }
    },
    [apiBase, session]
  );

  const loadSessionAccess = useCallback(async () => {
    if (!session) {
      setSessionAccess(initialLoadState());
      return;
    }

    setSessionAccess((current) => ({ ...current, loading: true, error: null }));
    try {
      const data = await runApi<SessionAccessResponse>("Session access", "/api/session");
      setSessionAccess({ loading: false, data, error: null });
    } catch (error) {
      const message = errorMessage(error);
      setSessionAccess({ loading: false, data: null, error: message });

      if (message === "forbidden" && supabase) {
        await supabase.auth.signOut();
        setAuthMessage("This account is not approved for Hermes Personal Assistant access.");
        setToday(initialLoadState());
        setActionItems(initialLoadState());
        setLinkedinItems(initialLoadState());
        setContextItems(initialLoadState());
        setDeliveries(initialLoadState());
        setStats(initialLoadState());
        navigateTo("login");
      }
    }
  }, [navigateTo, runApi, session]);

  const loadHealth = useCallback(async () => {
    setHealth((current) => ({ ...current, loading: true, error: null }));
    try {
      const data = await runApi<ApiHealth>("Health check", "/api/health", {}, false);
      setHealth({ loading: false, data, error: null });
    } catch (error) {
      setHealth({ loading: false, data: null, error: errorMessage(error) });
    }
  }, [runApi]);

  const loadToday = useCallback(async ({ showLoading = true }: LoadOptions = {}) => {
    if (showLoading) {
      setToday((current) => ({ ...current, loading: true, error: null }));
    } else {
      setToday((current) => ({ ...current, loading: false, error: null }));
    }
    try {
      const data = await runApi<TodayResponse>("Today", "/api/today");
      setToday({ loading: false, data, error: null });
    } catch (error) {
      setToday((current) => ({
        loading: false,
        data: showLoading ? null : current.data,
        error: showLoading || !current.data ? errorMessage(error) : null
      }));
    }
  }, [runApi]);

  const loadActionItems = useCallback(async ({ showLoading = true }: LoadOptions = {}) => {
    if (showLoading) {
      setActionItems((current) => ({ ...current, loading: true, error: null }));
    } else {
      setActionItems((current) => ({ ...current, loading: false, error: null }));
    }
    const params = new URLSearchParams({ status: actionStatus, review: actionReview, sort: "due_at", direction: "asc" });
    params.set("exclude_category", taskQueueExcludedCategories[0]);
    try {
      const data = await runApi<ActionItemsListResponse>("Tasks", `/api/action-items?${params}`);
      setActionItems({ loading: false, data, error: null });
    } catch (error) {
      setActionItems((current) => ({
        loading: false,
        data: showLoading ? null : current.data,
        error: showLoading || !current.data ? errorMessage(error) : null
      }));
    }
  }, [actionReview, actionStatus, runApi]);

  const loadLinkedinItems = useCallback(async ({ showLoading = true }: LoadOptions = {}) => {
    if (showLoading) {
      setLinkedinItems((current) => ({ ...current, loading: true, error: null }));
    } else {
      setLinkedinItems((current) => ({ ...current, loading: false, error: null }));
    }
    const params = new URLSearchParams({
      status: linkedinStatus,
      review: linkedinReview,
      category: linkedinActionItemCategory,
      sort: "created_at",
      direction: "desc"
    });

    try {
      const data = await runApi<ActionItemsListResponse>("LinkedIn", `/api/action-items?${params}`);
      setLinkedinItems({ loading: false, data, error: null });
    } catch (error) {
      setLinkedinItems((current) => ({
        loading: false,
        data: showLoading ? null : current.data,
        error: showLoading || !current.data ? errorMessage(error) : null
      }));
    }
  }, [linkedinReview, linkedinStatus, runApi]);

  const loadContextItems = useCallback(async ({ showLoading = true }: LoadOptions = {}) => {
    if (showLoading) {
      setContextItems((current) => ({ ...current, loading: true, error: null }));
    } else {
      setContextItems((current) => ({ ...current, loading: false, error: null }));
    }
    const params = new URLSearchParams({ status: contextStatus, sort: "starts_at", direction: "asc" });
    if (contextKind !== "all") {
      params.set("kind", contextKind);
    }

    try {
      const data = await runApi<ContextItemsListResponse>("Events", `/api/context-items?${params}`);
      setContextItems({ loading: false, data, error: null });
    } catch (error) {
      setContextItems((current) => ({
        loading: false,
        data: showLoading ? null : current.data,
        error: showLoading || !current.data ? errorMessage(error) : null
      }));
    }
  }, [contextKind, contextStatus, runApi]);

  const loadDeliveries = useCallback(async ({ showLoading = true }: LoadOptions = {}) => {
    if (showLoading) {
      setDeliveries((current) => ({ ...current, loading: true, error: null }));
    } else {
      setDeliveries((current) => ({ ...current, loading: false, error: null }));
    }
    const params = new URLSearchParams({ status: deliveryStatus, sort: "expected_at", direction: "asc" });

    try {
      const data = await runApi<DeliveriesListResponse>("Deliveries", `/api/deliveries?${params}`);
      setDeliveries({ loading: false, data, error: null });
    } catch (error) {
      setDeliveries((current) => ({
        loading: false,
        data: showLoading ? null : current.data,
        error: showLoading || !current.data ? errorMessage(error) : null
      }));
    }
  }, [deliveryStatus, runApi]);

  const loadStats = useCallback(async () => {
    setStats((current) => ({ ...current, loading: true, error: null }));
    try {
      const data = await runApi<StatsResponse>("Usage summary", "/api/stats");
      setStats({ loading: false, data, error: null });
    } catch (error) {
      setStats({ loading: false, data: null, error: errorMessage(error) });
    }
  }, [runApi]);

  const loadAuthenticatedData = useCallback(async ({ showLoading = true }: LoadOptions = {}) => {
    if (!session) {
      return;
    }
    await Promise.all([
      loadToday({ showLoading }),
      loadActionItems({ showLoading }),
      loadLinkedinItems({ showLoading }),
      loadContextItems({ showLoading }),
      loadDeliveries({ showLoading })
    ]);
  }, [loadActionItems, loadContextItems, loadDeliveries, loadLinkedinItems, loadToday, session]);

  const runPublicProbe = useCallback(async (label: string, path: string) => {
    try {
      await runApi<unknown>(label, path, {}, false);
    } catch {
      // The public observability denial checks intentionally expect 401 on data routes.
    }
  }, [runApi]);

  useEffect(() => {
    void loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    if (session) {
      void loadSessionAccess();
      return;
    }

    setSessionAccess(initialLoadState());
  }, [loadSessionAccess, session]);

  useEffect(() => {
    if (session && section === "app") {
      void loadAuthenticatedData();
    }
  }, [loadAuthenticatedData, section, session]);

  useEffect(() => {
    if (session && section === "observability") {
      void loadStats();
    }
  }, [loadStats, section, session]);

  useEffect(() => {
    if (session && section === "app") {
      void loadActionItems();
    }
  }, [actionReview, actionStatus, loadActionItems, section, session]);

  useEffect(() => {
    if (session && section === "app") {
      void loadLinkedinItems();
    }
  }, [linkedinReview, linkedinStatus, loadLinkedinItems, section, session]);

  useEffect(() => {
    if (session && section === "app") {
      void loadContextItems();
    }
  }, [contextKind, contextStatus, loadContextItems, section, session]);

  useEffect(() => {
    if (session && section === "app") {
      void loadDeliveries();
    }
  }, [deliveryStatus, loadDeliveries, section, session]);

  useEffect(() => {
    if (!supabase || !session?.access_token) {
      setRealtime(initialRealtimeState);
      return;
    }

    let mounted = true;
    let channel: ReturnType<SupabaseClient["channel"]> | null = null;

    setRealtime({ status: "connecting", message: "Connecting live updates…", lastSignalAt: null });

    runApi<RealtimeConfigResponse>("Realtime config", "/api/realtime-config")
      .then((realtimeConfig) => {
        if (!mounted) {
          return;
        }

        const eventName = realtimeConfig.events[0];
        supabase.realtime.setAuth(session.access_token);
        channel = supabase.channel(realtimeConfig.topic, {
          config: { private: realtimeConfig.private }
        });

        channel
          .on("broadcast", { event: eventName }, () => {
            const at = new Date().toISOString();
            setRealtime({ status: "live", message: "Assistant data changed. Refetched through the API.", lastSignalAt: at });
            void loadAuthenticatedData({ showLoading: false });
            if (section === "observability") {
              void loadStats();
            }
          })
          .subscribe((status, error) => {
            if (!mounted) {
              return;
            }

            if (status === "SUBSCRIBED") {
              setRealtime({ status: "live", message: "Live updates connected.", lastSignalAt: null });
              return;
            }

            if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
              setRealtime({
                status: "error",
                message: error?.message ?? `Live updates ${status.toLowerCase().replace("_", " ")}.`,
                lastSignalAt: null
              });
            }
          });
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }
        setRealtime({ status: "error", message: errorMessage(error), lastSignalAt: null });
      });

    return () => {
      mounted = false;
      if (channel) {
        void supabase.removeChannel(channel);
      }
    };
  }, [loadAuthenticatedData, loadStats, runApi, section, session?.access_token]);

  async function handleAuthSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAuthMessage(null);

    if (!supabase) {
      setAuthMessage("Client config is incomplete. Check the VITE_* environment variables.");
      return;
    }

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setAuthMessage("Enter an email address.");
      return;
    }

    const result = authMode === "magic"
      ? await supabase.auth.signInWithOtp({
          email: trimmedEmail,
          options: magicLinkSignInOptions(window.location.origin, "/app")
        })
      : await supabase.auth.signInWithPassword({ email: trimmedEmail, password });

    if (result.error) {
      setAuthMessage(`We couldn’t sign you in. ${result.error.message}`);
      navigateTo("login");
      return;
    }

    setAuthMessage(authMode === "magic" ? "Check your email for a sign-in link." : "Signed in. Opening your dashboard…");
    if (authMode === "password") {
      navigateTo("app");
    }
  }

  async function handleOAuthSignIn(provider: OAuthProvider) {
    setAuthMessage(null);
    if (!supabase) {
      setAuthMessage("Client config is incomplete. Check the VITE_* environment variables.");
      return;
    }

    if (providerAvailability[provider] === false) {
      setAuthMessage(provider === "apple" ? "Continue with Apple is coming soon." : "This sign-in option is not available right now.");
      navigateTo("login");
      return;
    }

    const redirectTo = oauthRedirectTo(window.location.origin, "/app");
    const result = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
        queryParams: provider === "google" ? { prompt: "select_account" } : undefined
      }
    });

    if (result.error) {
      setAuthMessage(`We couldn’t start ${provider === "google" ? "Google" : "Apple"} sign-in. ${result.error.message}`);
      navigateTo("login");
      return;
    }

    setAuthMessage(`Opening ${provider === "google" ? "Google" : "Apple"} sign-in…`);
  }

  async function signOut() {
    if (!supabase) {
      return;
    }
    await supabase.auth.signOut();
    setAuthMessage("You have been signed out.");
    setSessionAccess(initialLoadState());
    setToday(initialLoadState());
    setActionItems(initialLoadState());
    setLinkedinItems(initialLoadState());
    setContextItems(initialLoadState());
    setDeliveries(initialLoadState());
    setStats(initialLoadState());
    navigateTo("login");
  }

  async function mutateActionItem(id: string, action: "done" | "dismiss" | "reviewed" | "snooze") {
    if (!canMutateAssistantData) {
      setDebugEntry({
        label: `Action item ${action}`,
        method: "PATCH",
        path: `/api/action-items/${id}/${action}`,
        status: 403,
        ok: false,
        body: { error: "read_only_visual_review_session" },
        at: new Date().toISOString()
      });
      return;
    }

    const previousActionItems = actionItems;
    const previousLinkedinItems = linkedinItems;
    const previousToday = today;
    const existingRecord = findActionItem(id, actionItems.data, linkedinItems.data, today.data);
    const snoozeUntil = action === "snooze" ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() : undefined;
    const body = action === "snooze" ? JSON.stringify({ until: snoozeUntil }) : undefined;
    const taskFilters = { status: actionStatus, priorities: actionPriorities, review: actionReview, excludeCategory: taskQueueExcludedCategories[0] };
    const linkedinFilters = { status: linkedinStatus, priorities: [], review: linkedinReview, category: linkedinActionItemCategory };

    setPendingMutations((current) => [...current.filter((mutation) => !(mutation.kind === "action_items" && mutation.id === id)), { kind: "action_items", id, action }]);
    if (existingRecord) {
      const optimisticRecord = optimisticActionItem(existingRecord, action, snoozeUntil);
      setActionItems((current) => applyActionItemToList(current, optimisticRecord, taskFilters));
      setLinkedinItems((current) => applyActionItemToList(current, optimisticRecord, linkedinFilters));
      setToday((current) => applyActionItemToToday(current, optimisticRecord));
    }

    try {
      const response = await runApi<ActionItemLifecycleResponse>(`Action item ${action}`, `/api/action-items/${id}/${action}`, {
        method: "PATCH",
        body
      });
      setActionItems((current) => applyActionItemToList(current, response.record, taskFilters));
      setLinkedinItems((current) => applyActionItemToList(current, response.record, linkedinFilters));
      setToday((current) => applyActionItemToToday(current, response.record));
      void loadAuthenticatedData({ showLoading: false });
    } catch {
      setActionItems(previousActionItems);
      setLinkedinItems(previousLinkedinItems);
      setToday(previousToday);
    } finally {
      setPendingMutations((current) => current.filter((mutation) => !(mutation.kind === "action_items" && mutation.id === id)));
    }
  }

  async function mutateContextItem(id: string, action: "dismiss" | "done" | "reviewed") {
    if (!canMutateAssistantData) {
      setDebugEntry({
        label: `Event ${action}`,
        method: "PATCH",
        path: `/api/context-items/${id}/${action}`,
        status: 403,
        ok: false,
        body: { error: "read_only_visual_review_session" },
        at: new Date().toISOString()
      });
      return;
    }

    const previousContextItems = contextItems;
    const existingRecord = findContextItem(id, contextItems.data);
    const filters = { status: contextStatus, kind: contextKind };

    setPendingMutations((current) => [...current.filter((mutation) => !(mutation.kind === "context_items" && mutation.id === id)), { kind: "context_items", id, action }]);
    if (existingRecord) {
      const optimisticRecord = optimisticContextItem(existingRecord, action);
      setContextItems((current) => applyContextItemToList(current, optimisticRecord, filters));
    }

    try {
      const response = await runApi<ContextItemLifecycleResponse>(`Event ${action}`, `/api/context-items/${id}/${action}`, { method: "PATCH" });
      setContextItems((current) => applyContextItemToList(current, response.record, filters));
      void loadAuthenticatedData({ showLoading: false });
    } catch {
      setContextItems(previousContextItems);
    } finally {
      setPendingMutations((current) => current.filter((mutation) => !(mutation.kind === "context_items" && mutation.id === id)));
    }
  }

  async function mutateDelivery(id: string, action: "dismiss" | "delivered" | "delayed") {
    if (!canMutateAssistantData) {
      setDebugEntry({
        label: `Delivery ${action}`,
        method: "PATCH",
        path: action === "dismiss" ? `/api/deliveries/${id}/dismiss` : `/api/deliveries/${id}/status`,
        status: 403,
        ok: false,
        body: { error: "read_only_visual_review_session" },
        at: new Date().toISOString()
      });
      return;
    }

    const previousDeliveries = deliveries;
    const previousToday = today;
    const existingRecord = findDelivery(id, deliveries.data, today.data);
    const filters = { status: deliveryStatus };

    setPendingMutations((current) => [...current.filter((mutation) => !(mutation.kind === "deliveries" && mutation.id === id)), { kind: "deliveries", id, action }]);
    if (existingRecord) {
      const optimisticRecord = optimisticDelivery(existingRecord, action);
      setDeliveries((current) => applyDeliveryToList(current, optimisticRecord, filters));
      setToday((current) => applyDeliveryToToday(current, optimisticRecord));
    }

    try {
      const response = action === "dismiss"
        ? await runApi<DeliveryLifecycleResponse>("Dismiss delivery", `/api/deliveries/${id}/dismiss`, { method: "PATCH" })
        : await runApi<DeliveryLifecycleResponse>(`Delivery ${action}`, `/api/deliveries/${id}/status`, {
            method: "PATCH",
            body: JSON.stringify({ status: action })
          });
      setDeliveries((current) => applyDeliveryToList(current, response.record, filters));
      setToday((current) => applyDeliveryToToday(current, response.record));
      void loadAuthenticatedData({ showLoading: false });
    } catch {
      setDeliveries(previousDeliveries);
      setToday(previousToday);
    } finally {
      setPendingMutations((current) => current.filter((mutation) => !(mutation.kind === "deliveries" && mutation.id === id)));
    }
  }

  return (
    <main className={`app-shell section-${section}`}>
      <TopNav
        section={section}
        signedIn={signedIn}
        userEmail={userEmail}
        onNavigate={navigateTo}
        onSignOut={() => void signOut()}
      />

      {!clientConfigured ? <Notice tone="danger" title="Client config missing" message="Set VITE_API_BASE_URL, VITE_SUPABASE_URL, and VITE_SUPABASE_PUBLISHABLE_KEY before running the UI." /> : null}

      {section === "login" ? (
        <LoginPage
          authReady={authReady}
          session={session}
          userEmail={userEmail}
          email={email}
          password={password}
          authMode={authMode}
          authMessage={authMessage}
          providerAvailability={providerAvailability}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onAuthModeChange={setAuthMode}
          onSubmit={handleAuthSubmit}
          onOAuth={(provider) => void handleOAuthSignIn(provider)}
          onNavigate={navigateTo}
        />
      ) : null}

      {section === "observability" ? (
        <ObservabilityPage
          health={health}
          stats={stats}
          debugEntry={debugEntry}
          signedIn={signedIn}
          onRefreshHealth={() => void loadHealth()}
          onRefreshStats={() => void loadStats()}
          onRunHealth={() => void loadHealth()}
          onRunUnauthorizedToday={() => void runPublicProbe("Signed-out access check", "/api/today")}
          onRunUnauthorizedActions={() => void runPublicProbe("Signed-out task check", "/api/action-items")}
          onNavigate={navigateTo}
        />
      ) : null}

      {section === "app" ? (
        canAccessSection(section, signedIn) ? (
          <AssistantWorkspace
            today={today}
            actionItems={actionItems}
            linkedinItems={linkedinItems}
            contextItems={contextItems}
            deliveries={deliveries}
            actionStatus={actionStatus}
            actionPriorities={actionPriorities}
            actionReview={actionReview}
            linkedinStatus={linkedinStatus}
            linkedinReview={linkedinReview}
            contextStatus={contextStatus}
            contextKind={contextKind}
            deliveryStatus={deliveryStatus}
            access={sessionAccessLevel}
            canMutate={canMutateAssistantData}
            activeWorkQueue={activeWorkQueue}
            pendingMutations={pendingMutations}
            onActionStatusChange={setActionStatus}
            onActionPrioritiesChange={setActionPriorities}
            onActionReviewChange={setActionReview}
            onLinkedinStatusChange={setLinkedinStatus}
            onLinkedinReviewChange={setLinkedinReview}
            onContextStatusChange={setContextStatus}
            onContextKindChange={setContextKind}
            onDeliveryStatusChange={setDeliveryStatus}
            onActiveWorkQueueChange={setActiveWorkQueue}
            onRefreshAll={() => void loadAuthenticatedData()}
            onRefreshToday={() => void loadToday()}
            onRefreshActionItems={() => void loadActionItems()}
            onRefreshLinkedinItems={() => void loadLinkedinItems()}
            onRefreshContextItems={() => void loadContextItems()}
            onRefreshDeliveries={() => void loadDeliveries()}
            onMutateAction={(id, action) => void mutateActionItem(id, action)}
            onMutateContextItem={(id, action) => void mutateContextItem(id, action)}
            onMutateDelivery={(id, action) => void mutateDelivery(id, action)}
            onNavigate={navigateTo}
          />
        ) : (
          <ProtectedAppGate onNavigate={navigateTo} />
        )
      ) : null}
    </main>
  );
}

function TopNav(props: {
  section: AppSection;
  signedIn: boolean;
  userEmail: string;
  onNavigate: (section: AppSection) => void;
  onSignOut: () => void;
}) {
  const showPrimaryNav = props.signedIn;
  return (
    <header className={`top-nav glass-card ${props.section === "login" ? "top-nav-minimal" : ""}`}>
      <button className="brand-button" type="button" onClick={() => props.onNavigate(props.signedIn ? "app" : "login")}>
        <span className="brand-mark">H</span>
        <span>
          <strong>Hermes Personal Assistant</strong>
          <small>{props.signedIn ? "Assistant" : "Sign in"}</small>
        </span>
      </button>
      {showPrimaryNav ? (
        <nav aria-label="Primary navigation">
          <button type="button" className={props.section === "app" ? "active" : ""} onClick={() => props.onNavigate("app")}>Dashboard</button>
          <button type="button" className={props.section === "observability" ? "active" : ""} onClick={() => props.onNavigate("observability")}>Observability</button>
        </nav>
      ) : null}
      <div className="top-nav-actions">
        {props.signedIn ? (
          <AccountChip userEmail={props.userEmail} onSignOut={props.onSignOut} />
        ) : props.section === "observability" ? (
          <button className="top-link-button" type="button" onClick={() => props.onNavigate("login")}>Sign in</button>
        ) : null}
      </div>
    </header>
  );
}

function AccountChip({ userEmail, onSignOut }: { userEmail: string; onSignOut: () => void }) {
  const [localPart, domain = ""] = userEmail.split("@");
  const avatarLabel = localPart?.slice(0, 1).toUpperCase() || "H";
  return (
    <div className="account-chip" title={userEmail}>
      <span className="account-avatar" aria-hidden="true">{avatarLabel}</span>
      <span className="account-copy">
        <strong>{localPart || userEmail}</strong>
        {domain ? <small>{domain}</small> : null}
      </span>
      <button type="button" onClick={onSignOut}>Log out</button>
    </div>
  );
}

function LoginPage(props: AuthPanelProps) {
  return (
    <section className="login-screen">
      <AuthPanel {...props} />
    </section>
  );
}

type AuthPanelProps = {
  authReady: boolean;
  session: Session | null;
  userEmail: string;
  email: string;
  password: string;
  authMode: AuthMode;
  authMessage: string | null;
  providerAvailability: ProviderAvailability;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onAuthModeChange: (value: AuthMode) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onOAuth: (provider: OAuthProvider) => void;
  onNavigate: (section: AppSection) => void;
};

function AuthPanel(props: AuthPanelProps) {
  const switchingTo = props.authMode === "password" ? "magic" : "password";
  return (
    <section className="login-card glass-card" aria-labelledby="login-title">
      <div className="login-copy">
        <h1 id="login-title">Sign in</h1>
        <p>{props.authReady ? "Use your account to continue." : "Checking your session…"}</p>
      </div>

      {props.session ? (
        <div className="login-session-note">
          <span className="status-dot" data-on />
          <span>Signed in as {props.userEmail}. Opening your dashboard…</span>
        </div>
      ) : (
        <>
          <form className="auth-form login-form" onSubmit={props.onSubmit}>
            <label>
              Email address
              <input value={props.email} onChange={(event) => props.onEmailChange(event.target.value)} placeholder="you@example.com" autoComplete="email" type="email" required />
            </label>
            {props.authMode === "password" ? (
              <label>
                Password
                <input value={props.password} onChange={(event) => props.onPasswordChange(event.target.value)} type="password" autoComplete="current-password" required />
              </label>
            ) : (
              <p className="magic-link-help">We’ll email you a one-time sign-in link for this account.</p>
            )}
            <button className="primary-button full-width" type="submit">{props.authMode === "magic" ? "Email me a magic link" : "Sign in"}</button>
            <button className="auth-switch" type="button" onClick={() => props.onAuthModeChange(switchingTo)}>
              {props.authMode === "password" ? "Email me a magic link instead" : "Use password instead"}
            </button>
            {props.authMessage ? <p className="form-message" role="status">{props.authMessage}</p> : null}
          </form>

          <div className="divider"><span>or continue with</span></div>
          <div className="oauth-stack" aria-label="Social sign-in options">
            {oauthProviders.map((provider) => {
              const available = props.providerAvailability[provider] !== false;
              const label = provider === "apple" && !available ? "Continue with Apple" : oauthProviderLabel(provider);
              return (
                <button
                  className={`oauth-button oauth-${provider}`}
                  type="button"
                  key={provider}
                  onClick={() => props.onOAuth(provider)}
                  disabled={!available}
                >
                  <OAuthIcon provider={provider} />
                  <span className="provider-label">{label}</span>
                  {provider === "apple" && !available ? <span className="provider-status">Soon</span> : null}
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="login-footer">
        <button type="button" onClick={() => props.onNavigate("observability")}>View system status</button>
      </div>
    </section>
  );
}

function OAuthIcon({ provider }: { provider: OAuthProvider }) {
  if (provider === "google") {
    return (
      <svg className="provider-icon google-icon" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
        <path fill="#FFC107" d="M43.61 20.08H42V20H24v8h11.3C33.65 32.66 29.22 36 24 36c-6.63 0-12-5.37-12-12s5.37-12 12-12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.05 29.27 4 24 4 12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20c0-1.34-.14-2.63-.39-3.92Z" />
        <path fill="#FF3D00" d="m6.31 14.69 6.57 4.82C14.66 15.11 18.96 12 24 12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.05 29.27 4 24 4 16.32 4 9.66 8.34 6.31 14.69Z" />
        <path fill="#4CAF50" d="M24 44c5.17 0 9.86-1.98 13.41-5.19l-6.19-5.24C29.14 35.15 26.61 36 24 36c-5.2 0-9.62-3.31-11.28-7.93l-6.52 5.02C9.52 39.56 16.23 44 24 44Z" />
        <path fill="#1976D2" d="M43.61 20.08H42V20H24v8h11.3a12.04 12.04 0 0 1-4.08 5.57l6.19 5.24C36.97 39.21 44 34 44 24c0-1.34-.14-2.63-.39-3.92Z" />
      </svg>
    );
  }

  return (
    <svg className="provider-icon apple-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 8.65 7.31c1.25.07 2.12.69 2.85.75 1.1-.22 2.16-.86 3.32-.78 1.39.11 2.44.56 3.15 1.43-2.74 1.64-2.09 5.24.42 6.24-.5 1.32-1.15 2.63-2.34 3.33ZM12.02 7.25c-.15-1.96 1.46-3.58 3.29-3.75.25 2.1-1.88 3.66-3.29 3.75Z" />
    </svg>
  );
}

function ObservabilityPage(props: {
  health: LoadState<ApiHealth>;
  stats: LoadState<StatsResponse>;
  debugEntry: DebugEntry | null;
  signedIn: boolean;
  onRefreshHealth: () => void;
  onRefreshStats: () => void;
  onRunHealth: () => void;
  onRunUnauthorizedToday: () => void;
  onRunUnauthorizedActions: () => void;
  onNavigate: (section: AppSection) => void;
}) {
  return (
    <section className="route-layout observability-layout">
      <div className="section-heading compact-heading">
        <span className="mono-label">Observability</span>
        <h1>System status</h1>
        <p>Check service health and confirm private assistant data stays protected.</p>
        <div className="hero-actions">
          <button className="primary-button" type="button" onClick={props.onRefreshHealth}>Refresh status</button>
          <button className="ghost-button" type="button" onClick={() => props.onNavigate(props.signedIn ? "app" : "login")}>
            {props.signedIn ? "Back to dashboard" : "Back to sign in"}
          </button>
        </div>
      </div>
      <section className="dashboard-grid observability-grid">
        <HealthPanel state={props.health} onRefresh={props.onRefreshHealth} />
        <BoundaryPanel signedIn={props.signedIn} />
        <StatsPanel state={props.stats} onRefresh={props.onRefreshStats} signedIn={props.signedIn} />
        <DebugPanel
          entry={props.debugEntry}
          signedIn={props.signedIn}
          mode="observability"
          onRunHealth={props.onRunHealth}
          onRunToday={props.onRunUnauthorizedToday}
          onRunStats={props.onRefreshStats}
          onRunUnauthorizedActions={props.onRunUnauthorizedActions}
        />
      </section>
    </section>
  );
}

function BoundaryPanel({ signedIn }: { signedIn: boolean }) {
  return (
    <Panel title="Access checks" eyebrow="Privacy">
      <div className="boundary-list">
        <div><span className="status-pill status-delivered">Open</span><strong>Health check</strong><p>Public readiness only. No personal data is shown.</p></div>
        <div><span className="status-pill status-new">Blocked</span><strong>Signed-out data access</strong><p>Task and delivery routes must reject signed-out requests.</p></div>
        <div><span className="status-pill status-seen">Private</span><strong>Assistant workspace</strong><p>{signedIn ? "Available for this signed-in session." : "Available after sign-in."}</p></div>
      </div>
    </Panel>
  );
}

function AssistantWorkspace(props: {
  today: LoadState<TodayResponse>;
  actionItems: LoadState<ActionItemsListResponse>;
  linkedinItems: LoadState<ActionItemsListResponse>;
  contextItems: LoadState<ContextItemsListResponse>;
  deliveries: LoadState<DeliveriesListResponse>;
  actionStatus: string;
  actionPriorities: string[];
  actionReview: string;
  linkedinStatus: string;
  linkedinReview: string;
  contextStatus: string;
  contextKind: string;
  deliveryStatus: string;
  access: SessionAccess | null;
  canMutate: boolean;
  activeWorkQueue: WorkQueueTab;
  pendingMutations: PendingMutation[];
  onActionStatusChange: (value: string) => void;
  onActionPrioritiesChange: (value: string[]) => void;
  onActionReviewChange: (value: string) => void;
  onLinkedinStatusChange: (value: string) => void;
  onLinkedinReviewChange: (value: string) => void;
  onContextStatusChange: (value: string) => void;
  onContextKindChange: (value: string) => void;
  onDeliveryStatusChange: (value: string) => void;
  onActiveWorkQueueChange: (value: WorkQueueTab) => void;
  onRefreshAll: () => void;
  onRefreshToday: () => void;
  onRefreshActionItems: () => void;
  onRefreshLinkedinItems: () => void;
  onRefreshContextItems: () => void;
  onRefreshDeliveries: () => void;
  onMutateAction: (id: string, action: "done" | "dismiss" | "reviewed" | "snooze") => void;
  onMutateContextItem: (id: string, action: "dismiss" | "done" | "reviewed") => void;
  onMutateDelivery: (id: string, action: "dismiss" | "delivered" | "delayed") => void;
  onNavigate: (section: AppSection) => void;
}) {
  const priorityCounts = priorityCountsForActionItems(props.actionItems.data);
  const visibleTaskItems = actionItemsForPriorities(props.actionItems.data?.action_items ?? [], props.actionPriorities);
  const taskCount = visibleTaskItems.length;
  const linkedinCount = listCount(props.linkedinItems.data);
  const contextCount = contextListCount(props.contextItems.data);
  const deliveryCount = deliveryListCount(props.deliveries.data);

  return (
    <section className="route-layout app-layout">
      <div className="section-heading app-heading compact-heading">
        <div>
          <span className="mono-label">Dashboard</span>
          <h1>Your assistant</h1>
          <p>Review tasks, manage deliveries, and triage LinkedIn invites or opportunities.</p>
        </div>
      </div>
      {props.access === "visual_reviewer" ? (
        <Notice
          tone="info"
          title="Visual review mode"
          message="This signed-in reviewer session can inspect live assistant data, but task, delivery, and LinkedIn mutation controls are disabled."
        />
      ) : null}
      <section className="dashboard-grid app-grid">
        <WorkQueuesPanel
          activeTab={props.activeWorkQueue}
          actionItems={props.actionItems}
          linkedinItems={props.linkedinItems}
          contextItems={props.contextItems}
          deliveries={props.deliveries}
          taskCount={taskCount}
          linkedinCount={linkedinCount}
          contextCount={contextCount}
          deliveryCount={deliveryCount}
          signedIn
          actionStatus={props.actionStatus}
          actionPriorities={props.actionPriorities}
          priorityCounts={priorityCounts}
          actionReview={props.actionReview}
          linkedinStatus={props.linkedinStatus}
          linkedinReview={props.linkedinReview}
          contextStatus={props.contextStatus}
          contextKind={props.contextKind}
          deliveryStatus={props.deliveryStatus}
          canMutate={props.canMutate}
          pendingMutations={props.pendingMutations}
          onActiveTabChange={props.onActiveWorkQueueChange}
          onActionStatusChange={props.onActionStatusChange}
          onActionPrioritiesChange={props.onActionPrioritiesChange}
          onActionReviewChange={props.onActionReviewChange}
          onLinkedinStatusChange={props.onLinkedinStatusChange}
          onLinkedinReviewChange={props.onLinkedinReviewChange}
          onContextStatusChange={props.onContextStatusChange}
          onContextKindChange={props.onContextKindChange}
          onDeliveryStatusChange={props.onDeliveryStatusChange}
          onMutateAction={props.onMutateAction}
          onMutateContextItem={props.onMutateContextItem}
          onMutateDelivery={props.onMutateDelivery}
        />
      </section>
    </section>
  );
}

function ProtectedAppGate({ onNavigate }: { onNavigate: (section: AppSection) => void }) {
  return (
    <section className="protected-gate glass-card">
      <span className="mono-label">Sign in required</span>
      <h1>Please sign in</h1>
      <p>Your assistant dashboard is private. Sign in to manage tasks and deliveries.</p>
      <div className="hero-actions">
        <button className="primary-button" type="button" onClick={() => onNavigate("login")}>Sign in</button>
        <button className="ghost-button" type="button" onClick={() => onNavigate("observability")}>Observability</button>
      </div>
    </section>
  );
}
function HealthPanel({ state, onRefresh }: { state: LoadState<ApiHealth>; onRefresh: () => void }) {
  return (
    <Panel title="System health" eyebrow="Status" action={<button className="mini-button" onClick={onRefresh} type="button">Refresh</button>}>
      <StateFrame state={state} empty="Health has not been checked yet.">
        {(data) => (
          <div className="health-readout">
            <div className="big-status">{data.status}</div>
            <dl>
              <div><dt>Service</dt><dd>{data.service}</dd></div>
              <div><dt>Environment</dt><dd>{data.environment}</dd></div>
            </dl>
          </div>
        )}
      </StateFrame>
    </Panel>
  );
}

function StatsPanel({ state, onRefresh, signedIn }: { state: LoadState<StatsResponse>; onRefresh: () => void; signedIn: boolean }) {
  return (
    <Panel title="Usage summary" eyebrow="Observability" action={<button className="mini-button" onClick={onRefresh} disabled={!signedIn} type="button">Refresh</button>}>
      {!signedIn ? <LockedState /> : (
        <StateFrame state={state} empty="Usage summary will appear after sign-in.">
          {(data) => {
            const gmailFreshness = ingestFreshness(data.last_ingest_at);
            return (
              <div className="stats-grid">
                <Metric label="Active tasks" value={data.action_items.total_active} detail={`${data.action_items.requiring_review} need review`} />
                <Metric label="Due soon" value={data.action_items.due_soon + data.deliveries.due_soon} detail="Next 48 hours" />
                <Metric label="Active deliveries" value={data.deliveries.total_active} detail={`${data.deliveries.action_required} action required`} />
                <Metric label="Last Gmail ingest" value={gmailFreshness.value} detail={gmailFreshness.detail} tone={gmailFreshness.tone} />
                <Metric label="Database" value={data.readiness.database} detail="Available" />
              </div>
            );
          }}
        </StateFrame>
      )}
    </Panel>
  );
}


function WorkQueuesPanel(props: {
  activeTab: WorkQueueTab;
  actionItems: LoadState<ActionItemsListResponse>;
  linkedinItems: LoadState<ActionItemsListResponse>;
  contextItems: LoadState<ContextItemsListResponse>;
  deliveries: LoadState<DeliveriesListResponse>;
  taskCount: number;
  linkedinCount: number;
  contextCount: number;
  deliveryCount: number;
  signedIn: boolean;
  actionStatus: string;
  actionPriorities: string[];
  priorityCounts: Record<string, number>;
  actionReview: string;
  linkedinStatus: string;
  linkedinReview: string;
  contextStatus: string;
  contextKind: string;
  deliveryStatus: string;
  canMutate: boolean;
  pendingMutations: PendingMutation[];
  onActiveTabChange: (value: WorkQueueTab) => void;
  onActionStatusChange: (value: string) => void;
  onActionPrioritiesChange: (value: string[]) => void;
  onActionReviewChange: (value: string) => void;
  onLinkedinStatusChange: (value: string) => void;
  onLinkedinReviewChange: (value: string) => void;
  onContextStatusChange: (value: string) => void;
  onContextKindChange: (value: string) => void;
  onDeliveryStatusChange: (value: string) => void;
  onMutateAction: (id: string, action: "done" | "dismiss" | "reviewed" | "snooze") => void;
  onMutateContextItem: (id: string, action: "dismiss" | "done" | "reviewed") => void;
  onMutateDelivery: (id: string, action: "dismiss" | "delivered" | "delayed") => void;
}) {
  const isTasks = props.activeTab === "tasks";
  const isEvents = props.activeTab === "events";
  const isLinkedIn = props.activeTab === "linkedin";
  return (
    <Panel
      title="Work queues"
      eyebrow="Dashboard"
      className="work-queue-panel"
    >
      <QueueTabs activeTab={props.activeTab} taskCount={props.taskCount} contextCount={props.contextCount} linkedinCount={props.linkedinCount} deliveryCount={props.deliveryCount} onChange={props.onActiveTabChange} />
      {isTasks ? (
        <>
          <div className="filters task-filters">
            <div className="task-filter-row">
              <Select label="Status" value={props.actionStatus} options={actionItemStatusFilters} onChange={props.onActionStatusChange} />
              <Select label="Review" value={props.actionReview} options={["all", "needs_review", "reviewed"]} onChange={props.onActionReviewChange} />
            </div>
            <PriorityFilterChips selected={props.actionPriorities} counts={props.priorityCounts} onChange={props.onActionPrioritiesChange} />
          </div>
          {!props.signedIn ? <LockedState /> : (
            <StateFrame state={props.actionItems} empty="No action items match the selected filters.">
              {(data) => {
                const visibleItems = actionItemsForPriorities(data.action_items, props.actionPriorities);
                return visibleItems.length === 0 ? <EmptyState message="No action items match the selected priority chips." /> : (
                  <div className="item-list">
                    {visibleItems.map((item) => (
                      <ActionItemCard
                        key={item.id}
                        item={item}
                        canMutate={props.canMutate}
                        pendingAction={pendingMutationAction(props.pendingMutations, "action_items", item.id)}
                        onMutate={props.onMutateAction}
                      />
                    ))}
                  </div>
                );
              }}
            </StateFrame>
          )}
        </>
      ) : isEvents ? (
        <>
          <div className="filters context-filters">
            <Select label="Status" value={props.contextStatus} options={contextItemStatusFilters} onChange={props.onContextStatusChange} />
            <Select label="Kind" value={props.contextKind} options={["all", ...contextItemKinds]} onChange={props.onContextKindChange} />
          </div>
          {!props.signedIn ? <LockedState /> : (
            <StateFrame state={props.contextItems} empty="No event or reference context items match the selected filters.">
              {(data) => (
                <div className="item-list context-list">
                  {data.context_items.map((item) => (
                    <ContextItemCard
                      key={item.id}
                      item={item}
                      canMutate={props.canMutate}
                      pendingAction={pendingMutationAction(props.pendingMutations, "context_items", item.id)}
                      onMutate={props.onMutateContextItem}
                    />
                  ))}
                </div>
              )}
            </StateFrame>
          )}
        </>
      ) : isLinkedIn ? (
        <>
          <div className="filters linkedin-filters">
            <Select label="Status" value={props.linkedinStatus} options={actionItemStatusFilters} onChange={props.onLinkedinStatusChange} />
            <Select label="Review" value={props.linkedinReview} options={["all", "needs_review", "reviewed"]} onChange={props.onLinkedinReviewChange} />
          </div>
          {!props.signedIn ? <LockedState /> : (
            <StateFrame state={props.linkedinItems} empty="No LinkedIn invites or opportunity emails match the selected filters.">
              {(data) => (
                <div className="item-list linkedin-list">
                  {data.action_items.map((item) => (
                    <ActionItemCard
                      key={item.id}
                      item={item}
                      canMutate={props.canMutate}
                      pendingAction={pendingMutationAction(props.pendingMutations, "action_items", item.id)}
                      onMutate={props.onMutateAction}
                    />
                  ))}
                </div>
              )}
            </StateFrame>
          )}
        </>
      ) : (
        <>
          <div className="filters delivery-filters">
            <Select label="Status" value={props.deliveryStatus} options={deliveryStatusFilters} onChange={props.onDeliveryStatusChange} />
          </div>
          {!props.signedIn ? <LockedState /> : (
            <StateFrame state={props.deliveries} empty="No deliveries match the selected filters.">
              {(data) => (
                <div className="item-list deliveries-list">
                  {data.deliveries.map((delivery) => (
                    <DeliveryCard
                      key={delivery.id}
                      delivery={delivery}
                      canMutate={props.canMutate}
                      pendingAction={pendingMutationAction(props.pendingMutations, "deliveries", delivery.id)}
                      onMutate={props.onMutateDelivery}
                    />
                  ))}
                </div>
              )}
            </StateFrame>
          )}
        </>
      )}
    </Panel>
  );
}

function QueueTabs({ activeTab, taskCount, contextCount, linkedinCount, deliveryCount, onChange }: { activeTab: WorkQueueTab; taskCount: number; contextCount: number; linkedinCount: number; deliveryCount: number; onChange: (value: WorkQueueTab) => void }) {
  return (
    <div className="queue-tabs" role="tablist" aria-label="Work queues">
      <button type="button" role="tab" aria-selected={activeTab === "tasks"} className={activeTab === "tasks" ? "active" : ""} onClick={() => onChange("tasks")}>
        <span>Tasks</span>
        <strong>{taskCount}</strong>
      </button>
      <button type="button" role="tab" aria-selected={activeTab === "events"} className={activeTab === "events" ? "active" : ""} onClick={() => onChange("events")}>
        <span>Events</span>
        <strong>{contextCount}</strong>
      </button>
      <button type="button" role="tab" aria-selected={activeTab === "linkedin"} className={activeTab === "linkedin" ? "active" : ""} onClick={() => onChange("linkedin")}>
        <span>LinkedIn</span>
        <strong>{linkedinCount}</strong>
      </button>
      <button type="button" role="tab" aria-selected={activeTab === "deliveries"} className={activeTab === "deliveries" ? "active" : ""} onClick={() => onChange("deliveries")}>
        <span>Deliveries</span>
        <strong>{deliveryCount}</strong>
      </button>
    </div>
  );
}

function PriorityFilterChips({ selected, counts, onChange }: { selected: string[]; counts: Record<string, number>; onChange: (value: string[]) => void }) {
  function toggle(priority: string) {
    onChange(selected.includes(priority)
      ? selected.filter((entry) => entry !== priority)
      : [...selected, priority]);
  }

  return (
    <fieldset className="priority-chip-filter">
      <legend>Priority</legend>
      <div className="priority-chip-row" aria-label="Priority filters">
        {priorityChipOrder.map((priority) => {
          const active = selected.includes(priority);
          const count = counts[priority] ?? 0;
          return (
            <button
              key={priority}
              type="button"
              className={`priority-filter-chip priority-${priority} ${active ? "active" : ""}`}
              aria-pressed={active}
              onClick={() => toggle(priority)}
            >
              <span>{humanize(priority)}</span>
              <strong>{count}</strong>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function ContextItemCard({ item, canMutate, pendingAction, onMutate }: { item: ContextItemRow; canMutate: boolean; pendingAction: string | null; onMutate: (id: string, action: "dismiss" | "done" | "reviewed") => void }) {
  const disabled = !canMutate || Boolean(pendingAction);
  return (
    <article className={`record-card context-card ${pendingAction ? "record-card-pending" : ""}`}>
      <div className="record-main context-main">
        <div>
          <StatusPill value={item.status} />
          <h3>{item.title}</h3>
          <p>{item.summary}</p>
        </div>
        <span className="context-kind-badge">{humanize(item.kind)}</span>
      </div>
      <dl className="record-meta context-meta">
        <div><dt>When</dt><dd>{formatNullableDate(item.starts_at)}</dd></div>
        <div><dt>Where</dt><dd>{item.location ?? "Not captured"}</dd></div>
        <div><dt>Review</dt><dd>{item.requires_human_review ? "Needed" : "Cleared"}</dd></div>
      </dl>
      <div className="context-detail-grid">
        {item.details.length > 0 ? item.details.map((section) => (
          <details key={`${item.id}-${section.label}`} className="context-detail-section" open={item.details.length <= 4}>
            <summary>{section.label}</summary>
            <ul>
              {section.items.map((detail) => <li key={detail}>{detail}</li>)}
            </ul>
          </details>
        )) : <p className="context-empty-detail">No structured detail categories captured yet.</p>}
      </div>
      <div className="record-actions" aria-label={canMutate ? "Event actions" : "Event actions unavailable in visual review mode"}>
        <SourceEmailLink record={item} />
        <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(item.id, "reviewed")}>{pendingAction === "reviewed" ? "Updating…" : "Mark reviewed"}</button>
        <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(item.id, "done")}>{pendingAction === "done" ? "Updating…" : "Done"}</button>
        <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(item.id, "dismiss")}>{pendingAction === "dismiss" ? "Updating…" : "Dismiss"}</button>
      </div>
    </article>
  );
}

function SynopsisBlock({ summary }: { summary: string }) {
  const sections = synopsisSections(summary);
  return (
    <section className="record-synopsis" aria-label="Synopsis">
      <span>Synopsis</span>
      <dl className="synopsis-sections">
        {sections.map((section) => (
          <div className={`synopsis-section synopsis-${section.key}`} key={section.key}>
            <dt>{section.label}</dt>
            <dd>{section.key === "extraction-confidence" ? <span className="confidence-chip">{section.value}</span> : section.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function ActionItemCard({ item, canMutate, pendingAction, onMutate }: { item: ActionItemRow; canMutate: boolean; pendingAction: string | null; onMutate: (id: string, action: "done" | "dismiss" | "reviewed" | "snooze") => void }) {
  const disabled = !canMutate || Boolean(pendingAction);
  return (
    <article className={`record-card ${pendingAction ? "record-card-pending" : ""}`}>
      <div className="record-main">
        <div>
          <StatusPill value={item.status} />
          <h3>{item.title}</h3>
          <SynopsisBlock summary={item.summary} />
        </div>
        <span className={`priority-badge priority-${item.priority}`}>{item.priority}</span>
      </div>
      <dl className="record-meta">
        <div><dt>Category</dt><dd>{item.category}</dd></div>
        <div><dt>Due</dt><dd>{formatNullableDate(item.due_at)}</dd></div>
        <div><dt>Review</dt><dd>{item.requires_human_review ? "Needed" : "Cleared"}</dd></div>
      </dl>
      <div className="record-actions" aria-label={canMutate ? "Task actions" : "Task actions unavailable in visual review mode"}>
        <SourceEmailLink record={item} />
        <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(item.id, "reviewed")}>{pendingAction === "reviewed" ? "Updating…" : "Mark reviewed"}</button>
        <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(item.id, "snooze")}>{pendingAction === "snooze" ? "Updating…" : "Snooze 24 hours"}</button>
        <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(item.id, "done")}>{pendingAction === "done" ? "Updating…" : "Mark done"}</button>
        <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(item.id, "dismiss")}>{pendingAction === "dismiss" ? "Updating…" : "Dismiss"}</button>
      </div>
    </article>
  );
}

function DeliveryCard({ delivery, canMutate, pendingAction, onMutate }: { delivery: DeliveryRow; canMutate: boolean; pendingAction: string | null; onMutate: (id: string, action: "dismiss" | "delivered" | "delayed") => void }) {
  const disabled = !canMutate || Boolean(pendingAction);
  const [selectedStatus, setSelectedStatus] = useState<DeliveryTimelineEntry | null>(null);
  const sourceCount = delivery.source_message_ids?.length || delivery.gmail_message_ids?.length || 1;
  const timeline = deliveryTimeline(delivery);
  const current = timeline.find((entry) => entry.isCurrent) ?? timeline[timeline.length - 1] ?? null;
  const summary = deliveryCardSummary(delivery);

  return (
    <article className={`record-card delivery-card ${pendingAction ? "record-card-pending" : ""}`}>
      <div className="delivery-card-top">
        <div className="record-main delivery-summary">
          <div>
            <h3>{delivery.merchant ?? "Delivery"}</h3>
            <p className={`delivery-item-summary ${summary.fallback ? "delivery-item-summary-missing" : ""}`}>{summary.item}</p>
            {summary.reference ? <p className="delivery-reference-line">{summary.reference}</p> : null}
          </div>
        </div>
        <dl className="record-meta delivery-meta">
          <div><dt>Current</dt><dd>{current ? humanize(current.status) : humanize(delivery.status)}</dd></div>
          <div><dt>Expected</dt><dd>{formatNullableDate(delivery.expected_at)}</dd></div>
          <div><dt>Sources</dt><dd>{sourceCount}</dd></div>
        </dl>
        <div className="record-actions delivery-actions" aria-label={canMutate ? "Delivery actions" : "Delivery actions unavailable in visual review mode"}>
          <SourceEmailLink record={delivery} />
          <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(delivery.id, "delayed")}>{pendingAction === "delayed" ? "Updating…" : "Mark delayed"}</button>
          <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(delivery.id, "delivered")}>{pendingAction === "delivered" ? "Updating…" : "Delivered"}</button>
          <button type="button" disabled={disabled} title={buttonTitle(canMutate, pendingAction)} onClick={() => onMutate(delivery.id, "dismiss")}>{pendingAction === "dismiss" ? "Updating…" : "Dismiss"}</button>
        </div>
      </div>
      <section className="delivery-progress" aria-label="Delivery status progression">
        <div className="delivery-chip-list">
          {timeline.map((entry) => {
            const dateLabel = formatNullableDate(entry.at);
            const statusLabel = humanize(entry.status);
            const chipLabel = `${entry.isCurrent ? "Current" : "Previous"} status: ${statusLabel}. Updated ${dateLabel}. Click for source details.`;
            return (
              <button
                aria-label={chipLabel}
                className={`delivery-status-chip ${entry.isCurrent ? "delivery-status-chip-current" : "delivery-status-chip-past"}`}
                data-tooltip={dateLabel}
                key={entry.key}
                onClick={() => setSelectedStatus(entry)}
                title={chipLabel}
                type="button"
              >
                <DeliveryStatusPill value={entry.status} compact />
                <span className="sr-only">{chipLabel}</span>
              </button>
            );
          })}
        </div>
      </section>
      {selectedStatus ? <DeliveryStatusModal delivery={delivery} entry={selectedStatus} onClose={() => setSelectedStatus(null)} /> : null}
    </article>
  );
}

function DeliveryStatusModal({ delivery, entry, onClose }: { delivery: DeliveryRow; entry: DeliveryTimelineEntry; onClose: () => void }) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="delivery-status-modal" aria-modal="true" role="dialog" aria-labelledby={`delivery-status-${entry.key}`} onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="mono-label">Delivery status</span>
            <h3 id={`delivery-status-${entry.key}`}>{delivery.merchant ?? "Delivery"}</h3>
            <p>{delivery.tracking_reference ?? delivery.courier ?? "No tracking reference"}</p>
          </div>
          <button className="mini-button" onClick={onClose} type="button">Close</button>
        </div>
        <div className="modal-status-row">
          <DeliveryStatusPill value={entry.status} />
          <span>{entry.isCurrent ? "Current status" : "Status update"}</span>
        </div>
        <dl className="modal-detail-grid">
          <div><dt>Updated</dt><dd>{formatNullableDate(entry.at)}</dd></div>
          <div><dt>Expected</dt><dd>{formatNullableDate(entry.expected_at ?? delivery.expected_at)}</dd></div>
          <div><dt>Window</dt><dd>{entry.delivery_window ?? delivery.delivery_window ?? "—"}</dd></div>
          <div><dt>Source</dt><dd>{entry.source_message_id ?? entry.gmail_message_id ?? "Current delivery row"}</dd></div>
        </dl>
        <div className="modal-detail-body">
          <h4>Details</h4>
          <p>{entry.details ?? entry.summary ?? delivery.details ?? delivery.relevance_summary ?? "No structured source-mail detail is available for this status."}</p>
        </div>
        <div className="modal-actions">
          <SourceEmailLink record={entry} />
        </div>
      </section>
    </div>
  );
}


function DebugPanel({
  entry,
  signedIn,
  mode,
  onRunHealth,
  onRunToday,
  onRunStats,
  onRunUnauthorizedActions
}: {
  entry: DebugEntry | null;
  signedIn: boolean;
  mode: "observability";
  onRunHealth: () => void;
  onRunToday: () => void;
  onRunStats: () => void;
  onRunUnauthorizedActions?: () => void;
}) {
  return (
    <Panel title="Status checks" eyebrow={mode === "observability" ? "Observability" : "Checks"} className="span-two">
      <div className="debug-actions">
        <button className="mini-button" onClick={onRunHealth} type="button">Check health</button>
        <button className="mini-button" onClick={onRunToday} type="button">Test signed-out access</button>
        {onRunUnauthorizedActions ? <button className="mini-button" onClick={onRunUnauthorizedActions} type="button">Test task access</button> : null}
        <button className="mini-button" onClick={onRunStats} disabled={!signedIn} type="button">Refresh usage summary</button>
      </div>
      {entry ? (
        <div className="debug-output">
          <div className="debug-header">
            <strong>{entry.label}</strong>
            <span>{entry.method} {entry.path} → {entry.status ?? "network"}</span>
          </div>
          <pre>{JSON.stringify(entry.body, null, 2)}</pre>
        </div>
      ) : <EmptyState message="Run a status check to see the latest response." />}
    </Panel>
  );
}

function Panel({ title, eyebrow, children, action, className = "" }: { title: string; eyebrow: string; children: React.ReactNode; action?: React.ReactNode; className?: string }) {
  return (
    <section className={`panel glass-card ${className}`}>
      <div className="panel-header">
        <div>
          <span className="mono-label">{eyebrow}</span>
          <h2>{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function StateFrame<T>({ state, empty, children }: { state: LoadState<T>; empty: string; children: (data: T) => React.ReactNode }) {
  if (state.loading) {
    return <div className="loading-state"><span className="spinner" /> Loading…</div>;
  }

  if (state.error) {
    return <Notice tone="danger" title="Request failed" message={state.error} />;
  }

  if (!state.data || isEmptyPayload(state.data)) {
    return <EmptyState message={empty} />;
  }

  return <>{children(state.data)}</>;
}

function Select({ label, value, options, onChange }: { label: string; value: string; options: readonly string[]; onChange: (value: string) => void }) {
  return (
    <label className="filter-control">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option} value={option}>{humanize(option)}</option>)}
      </select>
    </label>
  );
}

function Metric({ label, value, detail, tone = "neutral" }: { label: string; value: string | number; detail: string; tone?: "neutral" | "ok" | "stale" }) {
  return (
    <div className={`metric-card metric-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </div>
  );
}

function StatusPill({ value }: { value: string }) {
  return <span className={`status-pill status-${value}`}>{humanize(value)}</span>;
}

function DeliveryStatusPill({ value, compact = false }: { value: string; compact?: boolean }) {
  return <span className={`delivery-status-pill delivery-status-${deliveryStatusTone(value)} ${compact ? "delivery-status-compact" : ""}`}>{humanize(value)}</span>;
}

function RealtimeIndicator({ state }: { state: RealtimeState }) {
  return (
    <div className={`realtime-indicator realtime-${state.status}`} title={state.lastSignalAt ? `Last signal ${formatNullableDate(state.lastSignalAt)}` : state.message}>
      <span className="live-dot" aria-hidden="true" />
      <div>
        <strong>{state.status === "live" ? "Live" : humanize(state.status)}</strong>
        <span>{state.lastSignalAt ? `Updated ${formatTime(state.lastSignalAt)}` : state.message}</span>
      </div>
    </div>
  );
}

type GmailSourceRecord = {
  source_url?: string | null;
  gmail_message_id?: string | null;
  gmail_thread_id?: string | null;
  gmail_rfc822_message_id?: string | null;
};

function SourceEmailLink({ record }: { record: GmailSourceRecord }) {
  const href = gmailUrlForRecord(record);
  if (!href) {
    return null;
  }
  return (
    <a className="source-email-link" href={href} target="_blank" rel="noopener noreferrer" aria-label="Open source email in Gmail">
      Open in Gmail
    </a>
  );
}

function gmailUrlForRecord(record: GmailSourceRecord): string | null {
  const explicitUrl = safeGmailUrl(record.source_url);
  if (explicitUrl) {
    return explicitUrl;
  }
  const rfc822MessageId = record.gmail_rfc822_message_id?.trim();
  if (rfc822MessageId) {
    return `https://mail.google.com/mail/u/0/#search/${encodeURIComponent(`rfc822msgid:${rfc822MessageId}`)}`;
  }
  const threadOrMessageId = record.gmail_thread_id?.trim() || record.gmail_message_id?.trim();
  if (!threadOrMessageId) {
    return null;
  }
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(threadOrMessageId)}`;
}

function safeGmailUrl(value: string | null | undefined): string | null {
  const cleaned = value?.trim();
  if (!cleaned) {
    return null;
  }
  try {
    const url = new URL(cleaned);
    if (url.protocol !== "https:" || url.hostname !== "mail.google.com") {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

function pendingMutationAction(mutations: PendingMutation[], kind: PendingMutationKind, id: string): string | null {
  return mutations.find((mutation) => mutation.kind === kind && mutation.id === id)?.action ?? null;
}

type ActionItemListFilters = { status: string; priorities: string[]; review: string; category?: string; excludeCategory?: string };
type ContextItemListFilters = { status: string; kind: string };

function actionItemMatchesFilters(record: ActionItemRow, filters: ActionItemListFilters): boolean {
  const statusMatches = filters.status === "all"
    || (filters.status === "active" ? activeActionItemStatuses.includes(record.status as (typeof activeActionItemStatuses)[number]) : record.status === filters.status);
  const priorityMatches = filters.priorities.length === 0 || filters.priorities.includes(record.priority);
  const categoryMatches = filters.category ? record.category === filters.category : true;
  const excludedCategoryMatches = filters.excludeCategory ? record.category !== filters.excludeCategory : true;
  const reviewMatches = filters.review === "all"
    || (filters.review === "needs_review" ? record.requires_human_review : !record.requires_human_review);
  return statusMatches && priorityMatches && categoryMatches && excludedCategoryMatches && reviewMatches;
}

function deliveryMatchesFilters(record: DeliveryRow, filters: { status: string }): boolean {
  return filters.status === "all"
    || (filters.status === "active" ? activeDeliveryStatuses.includes(record.status as (typeof activeDeliveryStatuses)[number]) : record.status === filters.status);
}

function contextItemMatchesFilters(record: ContextItemRow, filters: ContextItemListFilters): boolean {
  const statusMatches = filters.status === "all"
    || (filters.status === "active" ? activeContextItemStatuses.includes(record.status as (typeof activeContextItemStatuses)[number]) : record.status === filters.status);
  const kindMatches = filters.kind === "all" || record.kind === filters.kind;
  return statusMatches && kindMatches;
}

function applyActionItemToList(
  state: LoadState<ActionItemsListResponse>,
  record: ActionItemRow,
  filters: ActionItemListFilters
): LoadState<ActionItemsListResponse> {
  if (!state.data) {
    return state;
  }
  const visible = actionItemMatchesFilters(record, filters);
  const exists = state.data.action_items.some((item) => item.id === record.id);
  const nextItems = visible
    ? exists
      ? state.data.action_items.map((item) => (item.id === record.id ? record : item))
      : [record, ...state.data.action_items]
    : state.data.action_items.filter((item) => item.id !== record.id);

  return {
    loading: false,
    error: null,
    data: {
      ...state.data,
      action_items: nextItems,
      pagination: updatePaginationForLength(state.data.pagination, state.data.action_items.length, nextItems.length)
    }
  };
}

function applyDeliveryToList(
  state: LoadState<DeliveriesListResponse>,
  record: DeliveryRow,
  filters: { status: string }
): LoadState<DeliveriesListResponse> {
  if (!state.data) {
    return state;
  }
  const visible = deliveryMatchesFilters(record, filters);
  const exists = state.data.deliveries.some((delivery) => delivery.id === record.id);
  const nextDeliveries = visible
    ? exists
      ? state.data.deliveries.map((delivery) => (delivery.id === record.id ? record : delivery))
      : [record, ...state.data.deliveries]
    : state.data.deliveries.filter((delivery) => delivery.id !== record.id);

  return {
    loading: false,
    error: null,
    data: {
      ...state.data,
      deliveries: nextDeliveries,
      pagination: updatePaginationForLength(state.data.pagination, state.data.deliveries.length, nextDeliveries.length)
    }
  };
}

function applyContextItemToList(
  state: LoadState<ContextItemsListResponse>,
  record: ContextItemRow,
  filters: ContextItemListFilters
): LoadState<ContextItemsListResponse> {
  if (!state.data) {
    return state;
  }
  const visible = contextItemMatchesFilters(record, filters);
  const exists = state.data.context_items.some((item) => item.id === record.id);
  const nextItems = visible
    ? exists
      ? state.data.context_items.map((item) => (item.id === record.id ? record : item))
      : [record, ...state.data.context_items]
    : state.data.context_items.filter((item) => item.id !== record.id);

  return {
    loading: false,
    error: null,
    data: {
      ...state.data,
      context_items: nextItems,
      pagination: updatePaginationForLength(state.data.pagination, state.data.context_items.length, nextItems.length)
    }
  };
}

function applyActionItemToToday(state: LoadState<TodayResponse>, record: ActionItemRow): LoadState<TodayResponse> {
  if (!state.data || !state.data.action_items.some((item) => item.id === record.id)) {
    return state;
  }
  const isActive = activeActionItemStatuses.includes(record.status as (typeof activeActionItemStatuses)[number]);
  return {
    loading: false,
    error: null,
    data: {
      ...state.data,
      action_items: isActive
        ? state.data.action_items.map((item) => (item.id === record.id ? record : item))
        : state.data.action_items.filter((item) => item.id !== record.id)
    }
  };
}

function applyDeliveryToToday(state: LoadState<TodayResponse>, record: DeliveryRow): LoadState<TodayResponse> {
  if (!state.data || !state.data.deliveries.some((delivery) => delivery.id === record.id)) {
    return state;
  }
  const isActive = activeDeliveryStatuses.includes(record.status as (typeof activeDeliveryStatuses)[number]);
  return {
    loading: false,
    error: null,
    data: {
      ...state.data,
      deliveries: isActive
        ? state.data.deliveries.map((delivery) => (delivery.id === record.id ? record : delivery))
        : state.data.deliveries.filter((delivery) => delivery.id !== record.id)
    }
  };
}

function listCount(data: ActionItemsListResponse | null): number {
  return data?.pagination.total ?? data?.action_items.length ?? 0;
}

function contextListCount(data: ContextItemsListResponse | null): number {
  return data?.pagination.total ?? data?.context_items.length ?? 0;
}

function deliveryListCount(data: DeliveriesListResponse | null): number {
  return data?.pagination.total ?? data?.deliveries.length ?? 0;
}

function priorityCountsForActionItems(data: ActionItemsListResponse | null): Record<string, number> {
  const counts: Record<string, number> = Object.fromEntries(actionItemPriorities.map((priority) => [priority, 0]));
  for (const item of data?.action_items ?? []) {
    counts[item.priority] = (counts[item.priority] ?? 0) + 1;
  }
  return counts;
}

function actionItemsForPriorities(items: ActionItemRow[], selected: string[]): ActionItemRow[] {
  return selected.length === 0 ? items : items.filter((item) => selected.includes(item.priority));
}

function updatePaginationForLength<T extends { returned: number; total: number | null }>(pagination: T, previousLength: number, nextLength: number): T {
  const delta = nextLength - previousLength;
  return {
    ...pagination,
    returned: nextLength,
    total: pagination.total === null ? null : Math.max(0, pagination.total + delta)
  };
}

function optimisticActionItem(record: ActionItemRow, action: "done" | "dismiss" | "reviewed" | "snooze", snoozeUntil?: string): ActionItemRow {
  const updatedAt = new Date().toISOString();
  if (action === "done") {
    return { ...record, status: "done", updated_at: updatedAt };
  }
  if (action === "dismiss") {
    return { ...record, status: "dismissed", updated_at: updatedAt };
  }
  if (action === "reviewed") {
    return { ...record, requires_human_review: false, updated_at: updatedAt };
  }
  return { ...record, status: "snoozed", due_at: snoozeUntil ?? record.due_at, updated_at: updatedAt };
}

function optimisticDelivery(record: DeliveryRow, action: "dismiss" | "delivered" | "delayed"): DeliveryRow {
  const updatedAt = new Date().toISOString();
  if (action === "dismiss") {
    return { ...record, status: "dismissed", updated_at: updatedAt, last_update_at: updatedAt };
  }
  if (action === "delivered") {
    return { ...record, status: "delivered", updated_at: updatedAt, last_update_at: updatedAt };
  }
  return { ...record, status: "delayed", updated_at: updatedAt, last_update_at: updatedAt };
}

function optimisticContextItem(record: ContextItemRow, action: "dismiss" | "done" | "reviewed"): ContextItemRow {
  const updatedAt = new Date().toISOString();
  if (action === "dismiss") {
    return { ...record, status: "dismissed", requires_human_review: false, updated_at: updatedAt, last_update_at: updatedAt };
  }
  if (action === "done") {
    return { ...record, status: "done", requires_human_review: false, updated_at: updatedAt, last_update_at: updatedAt };
  }
  return { ...record, status: "seen", requires_human_review: false, updated_at: updatedAt };
}

function findActionItem(id: string, actionItems: ActionItemsListResponse | null, linkedinItems: ActionItemsListResponse | null, today: TodayResponse | null): ActionItemRow | null {
  return actionItems?.action_items.find((item) => item.id === id)
    ?? linkedinItems?.action_items.find((item) => item.id === id)
    ?? today?.action_items.find((item) => item.id === id)
    ?? null;
}

function findDelivery(id: string, deliveries: DeliveriesListResponse | null, today: TodayResponse | null): DeliveryRow | null {
  return deliveries?.deliveries.find((delivery) => delivery.id === id)
    ?? today?.deliveries.find((delivery) => delivery.id === id)
    ?? null;
}

function findContextItem(id: string, contextItems: ContextItemsListResponse | null): ContextItemRow | null {
  return contextItems?.context_items.find((item) => item.id === id) ?? null;
}

function buttonTitle(canMutate: boolean, pendingAction: string | null): string | undefined {
  if (pendingAction) {
    return "Updating this item…";
  }
  return readOnlyButtonTitle(canMutate);
}

function LockedState() {
  return <EmptyState message="Sign in to view this information." />;
}

function EmptyState({ message }: { message: string }) {
  return <div className="empty-state">{message}</div>;
}

function Notice({ title, message, tone }: { title: string; message: string; tone: "danger" | "info" }) {
  return (
    <div className={`notice notice-${tone}`}>
      <strong>{title}</strong>
      <p>{message}</p>
    </div>
  );
}

function isEmptyPayload(value: unknown): boolean {
  if (!value || typeof value !== "object") {
    return !value;
  }

  if ("action_items" in value && Array.isArray((value as { action_items: unknown[] }).action_items)) {
    return (value as { action_items: unknown[] }).action_items.length === 0
      && (!("deliveries" in value) || (value as { deliveries?: unknown[] }).deliveries?.length === 0);
  }

  if ("deliveries" in value && Array.isArray((value as { deliveries: unknown[] }).deliveries)) {
    return (value as { deliveries: unknown[] }).deliveries.length === 0;
  }

  if ("context_items" in value && Array.isArray((value as { context_items: unknown[] }).context_items)) {
    return (value as { context_items: unknown[] }).context_items.length === 0;
  }

  if ("commands" in value && Array.isArray((value as { commands: unknown[] }).commands)) {
    return (value as { commands: unknown[] }).commands.length === 0;
  }

  return false;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unexpected request failure.";
}

function readOnlyButtonTitle(canMutate: boolean): string | undefined {
  return canMutate ? undefined : "Disabled for read-only visual review sessions.";
}

function humanize(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (match) => match.toUpperCase());
}

function deliveryStatusTone(value: string): "positive" | "warning" | "negative" {
  if (["cancelled", "canceled", "dismissed", "failed", "expired"].includes(value)) {
    return "negative";
  }
  return "positive";
}

function ingestFreshness(value: string | null): { value: string; detail: string; tone: "ok" | "stale" } {
  if (!value) {
    return { value: "No ingest", detail: "No Gmail rows yet", tone: "stale" };
  }
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) {
    return { value: "Unknown", detail: "Invalid timestamp", tone: "stale" };
  }
  const ageHours = (Date.now() - timestamp) / (1000 * 60 * 60);
  if (ageHours > 24) {
    return { value: "Stale", detail: `Last ${formatNullableDate(value)}`, tone: "stale" };
  }
  return { value: "Fresh", detail: `Last ${formatNullableDate(value)}`, tone: "ok" };
}

function formatNullableDate(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }).format(new Date(value));
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
